import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, LockClosedIcon, UserIcon, PhoneIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { Button, Input, Select } from '../../../components';
import { useAuth } from '../hooks/useAuth';

export default function RegisterForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    store_name: '',
    platform_type: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { register, isLoading } = useAuth();

  const platformOptions = [
    { value: 'shopify', label: 'Shopify' },
    { value: 'woocommerce', label: 'WooCommerce' },
    { value: 'custom', label: 'Custom' },
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.store_name.trim()) {
      newErrors.store_name = 'Store name is required';
    }

    if (!formData.platform_type) {
      newErrors.platform_type = 'Platform type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    register({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      phone: formData.phone || undefined,
      store_name: formData.store_name,
      platform_type: formData.platform_type,
    });
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div className="w-full max-w-md">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-ink font-display">Create account</h1>
        <p className="mt-2 text-sm text-ink-2">
          Join the customer support team
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <Input
          type="text"
          label="Full name"
          placeholder="Jane Smith"
          value={formData.name}
          onChange={handleChange('name')}
          error={errors.name}
          leftIcon={<UserIcon className="h-5 w-5" />}
          autoComplete="name"
          autoFocus
        />

        <Input
          type="email"
          label="Email address"
          placeholder="you@example.com"
          value={formData.email}
          onChange={handleChange('email')}
          error={errors.email}
          leftIcon={<EnvelopeIcon className="h-5 w-5" />}
          autoComplete="email"
        />

        <Input
          type="tel"
          label="Phone number (optional)"
          placeholder="With country code, e.g. +44 7700 900123"
          value={formData.phone}
          onChange={handleChange('phone')}
          error={errors.phone}
          leftIcon={<PhoneIcon className="h-5 w-5" />}
          autoComplete="tel"
        />

        <Input
          type="text"
          label="Store name"
          placeholder="Your shop's name"
          value={formData.store_name}
          onChange={handleChange('store_name')}
          error={errors.store_name}
          leftIcon={<BuildingStorefrontIcon className="h-5 w-5" />}
          autoComplete="organization"
        />

        <Select
          label="Platform"
          placeholder="Select your platform"
          value={formData.platform_type}
          onChange={(value) => setFormData((prev) => ({ ...prev, platform_type: value }))}
          options={platformOptions}
          error={errors.platform_type}
        />

        <Input
          type="password"
          label="Password"
          placeholder="Create a password"
          value={formData.password}
          onChange={handleChange('password')}
          error={errors.password}
          leftIcon={<LockClosedIcon className="h-5 w-5" />}
          autoComplete="new-password"
        />

        <Input
          type="password"
          label="Confirm password"
          placeholder="Re-enter your password"
          value={formData.confirmPassword}
          onChange={handleChange('confirmPassword')}
          error={errors.confirmPassword}
          leftIcon={<LockClosedIcon className="h-5 w-5" />}
          autoComplete="new-password"
        />

        <div className="flex items-start">
          <input
            id="terms"
            name="terms"
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-line bg-surface text-brand focus:ring-brand"
            required
          />
          <label htmlFor="terms" className="ml-2 block text-sm text-ink-2">
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="font-medium text-brand hover:text-brand-ink">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" className="font-medium text-brand hover:text-brand-ink">
              Privacy Policy
            </Link>
          </label>
        </div>

        <Button type="submit" className="w-full" isLoading={isLoading}>
          Create account
        </Button>

        <p className="text-center text-sm text-ink-2">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand hover:text-brand-ink">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
